import { useEffect, useMemo, useRef, useState } from 'react';

import {
	Alert,
	Button,
	Card,
	Divider,
	Empty,
	Input,
	InputNumber,
	Modal,
	Result,
	Select,
	Spin,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import {
	ArrowLeftOutlined,
	CheckCircleOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';

import BuildingTag from '../../../components/BuildingTag';
import { useBuildingContext } from '../../../context/BuildingContext';
import { MaterialShapePreview } from '../../../components/Dimensions/MaterialShapePreview';
import TabletHeader from '../../../components/Operations/TabletHeader';
import {
	TABLET_BACK_BUTTON_STYLE,
	TABLET_SEARCH_STYLE,
} from '../../../components/Operations/tabletStyles';
import { usePersistedUser } from '../../../hooks/usePersistedUser';
import {
	CandidateBar,
	CandidateBarLocation,
	CandidateBucket,
	CandidatesResponse,
	CreateReservationResponse,
	NeedOrderLineItem,
	ShopFloorMaterialNeed,
	SourceReservationSpec,
	useCreateShopFloorReservationMutation,
	useDismissShopFloorMaterialNeedMutation,
	useGetMaterialNeedCutOptionsQuery,
	useGetShopFloorMaterialNeedsQuery,
	useLazyGetMaterialNeedCandidatesQuery,
	useLazySearchReservableJobsQuery,
} from '../../../redux/api/shop-floor-needs';
import { type LocationLabelFormatter, labelOrShortCode, useLocationLabel } from '../../../utils/locationLabel';

const { Paragraph, Text, Title } = Typography;

// Saw Cut Detail's label/value convention, so a number means the same thing
// and reads the same way on both pages.
const LABEL_COLOR = '#888';
const CARD_TITLE = { fontSize: 17 };
const THUMBNAIL_SIZE = 56;

const jobLabel = (job?: NeedOrderLineItem | null) =>
	job ? `${job.order_name || 'Unknown job'} — ${job.name || 'part'}` : '';

const inches = (value?: string | number | null) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? `${parsed.toFixed(1)} in.` : '—';
};

// The saw cut queue says "21.6 in left" for a bar's remaining stock, so the
// same number reads the same way here.
const inchesLeft = (value?: string | number | null) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? `${parsed.toFixed(1)} in left` : '—';
};

const barId = (bar: CandidateBar) =>
	[bar.bundle_friendly_id, bar.friendly_id].filter(Boolean).join(' | ') || '—';

const relativeAge = (isoTime: string) => {
	const minutes = Math.round((Date.now() - new Date(isoTime).getTime()) / 60000);
	if (minutes < 1) return 'just now';
	if (minutes < 60) return `${minutes} min ago`;
	const hours = Math.round(minutes / 60);
	if (hours < 24) return `${hours} hr ago`;
	const days = Math.round(hours / 24);
	return days === 1 ? 'yesterday' : `${days} days ago`;
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
	label,
	children,
}) => (
	<span style={{ fontSize: 15 }}>
		<span style={{ color: LABEL_COLOR }}>{label}</span>{' '}
		<strong style={{ color: '#222' }}>{children}</strong>
	</span>
);

/**
 * A section label above its control. Deliberately a block: as a `<span>` it
 * shared the line with `Input.Search`'s inline-block wrapper and the text ran
 * in behind the box.
 */
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div style={{ fontSize: 14, color: LABEL_COLOR, marginBottom: 6 }}>
		{children}
	</div>
);

/**
 * Small part image so the operator can recognise the shape before reading the
 * job number. Falls back to a neutral box rather than a broken image, because
 * not every job has a thumbnail on file.
 */
const PartThumbnail: React.FC<{
	job: NeedOrderLineItem;
	size?: number;
}> = ({ job, size = THUMBNAIL_SIZE }) => {
	const [failed, setFailed] = useState(false);
	const frame: React.CSSProperties = {
		width: size,
		height: size,
		flexShrink: 0,
		border: '1px solid #f0f0f0',
		borderRadius: 6,
		background: '#fafafa',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
	};

	if (!job.thumbnail_url || failed) {
		return (
			<div style={frame}>
				<span style={{ fontSize: 11, color: '#bfbfbf' }}>No image</span>
			</div>
		);
	}

	return (
		<div style={frame}>
			<img
				src={job.thumbnail_url}
				alt={job.name || 'Part'}
				onError={() => setFailed(true)}
				style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
			/>
		</div>
	);
};

/**
 * ERP stores the scheduler's plan and the floor's actual choice in the same
 * column, so the machine name is never shown without saying which one it is.
 *
 * `reported` is the machine the mill operator named when raising the request.
 * ERP often has no assignment of its own, and this is the saw operator's only
 * clue about where to walk the material, so it is shown -- labelled, because it
 * is a claim from the floor rather than anything ERP verified.
 */
const MachineTag: React.FC<{
	job: NeedOrderLineItem;
	reported?: string | null;
}> = ({ job, reported }) => {
	if (!job.machine_name) {
		if (!reported) return <>—</>;
		return (
			<>
				{reported}{' '}
				<Tag color="orange" style={{ marginInlineEnd: 0 }}>
					per request
				</Tag>
			</>
		);
	}
	return (
		<>
			{job.machine_name}{' '}
			{job.machine_status === 'on_machine_now' ? (
				<Tag color="green" style={{ marginInlineEnd: 0 }}>
					on machine now
				</Tag>
			) : job.machine_status === 'scheduled' ? (
				<Tag color="blue" style={{ marginInlineEnd: 0 }}>
					scheduled
				</Tag>
			) : null}
		</>
	);
};

/**
 * `MaterialShapePreview` reads the nested shape the material endpoints return.
 * The candidate payload is flat, so adapt here rather than reshaping the API --
 * that keeps one component responsible for drawing every material in the app.
 */
const toPreviewRecord = (source: CandidateBar | SourceReservationSpec) => ({
	material_shape: { display_name: source.shape },
	material_type: {
		display_name: source.type,
		material_category: { display_name: source.category },
	},
	height: source.height,
	width: source.width,
	outer_diameter: source.outer_diameter,
	inner_diameter: source.inner_diameter,
	wall_thickness: source.wall_thickness,
});

const ShapePreview: React.FC<{
	source: CandidateBar | SourceReservationSpec;
	width?: number;
	height?: number;
}> = ({ source, width = 78, height = 36 }) => (
	<svg
		width={width}
		height={height}
		viewBox="0 0 100 44"
		role="img"
		aria-label={`${source.shape || 'Material'} preview`}
		style={{ flexShrink: 0 }}
	>
		<MaterialShapePreview record={toPreviewRecord(source)} />
	</svg>
);

/** Operators call a slot "X1-A6"; shared shelves keep theirs ("F1-Shelf 2"). */
const BarLocation: React.FC<{
	location: CandidateBarLocation;
	size?: number;
}> = ({ location, size = 18 }) => {
	const formatLocation = useLocationLabel();
	return (
		<div style={{ fontSize: size, fontWeight: 600, lineHeight: 1.2 }}>
			{barLocationLabel(location, formatLocation) || 'No location'}
		</div>
	);
};

const barLocationLabel = (location: CandidateBarLocation, format: LocationLabelFormatter) =>
	labelOrShortCode(location.display_name, location.short_code, format);

/** One bar already committed in this session, for the progress strip. */
interface CommittedBar {
	label: string;
	location: string;
	pieces: number;
}

/**
 * Reservations: the hot list of material requests raised from the mills, and
 * the flow that turns one of them into a real reservation.
 *
 * These were two pages. They asked the same question -- which job needs
 * material -- so the list is now the landing state of one page and picking a
 * request collapses it into the job summary. Both routes still resolve here so
 * existing deep links keep working.
 *
 * A request is not a reservation: the mill operator has no way to pick the
 * right bar, so all they send is the job, a piece count and a reason. A saw
 * operator turns that into a reservation from the lower half of this screen.
 */
const Reservations: React.FC = () => {
	const navigate = useNavigate();
	const formatLocation = useLocationLabel();
	const [params, setParams] = useSearchParams();
	const needId = params.get('need');
	const cameFromCutting = params.get('from') === 'cutting';

	// Tablet pages share a login, so the person is picked from localStorage.
	// Without sending it explicitly, "who reserved this?" has no answer.
	const [selectedUser] = usePersistedUser('Machine Operators', 'selectedUser');

	const [hotListSearch, setHotListSearch] = useState('');
	const [dismissing, setDismissing] = useState<ShopFloorMaterialNeed | null>(
		null
	);

	const [job, setJob] = useState<NeedOrderLineItem | null>(null);
	const [quantity, setQuantity] = useState<number>(1);
	const [sourceReservation, setSourceReservation] = useState<string | undefined>();
	const [showOtherOptions, setShowOtherOptions] = useState(false);
	const [pendingBar, setPendingBar] = useState<CandidateBar | null>(null);
	const [certBar, setCertBar] = useState<CandidateBar | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [jobSearchTerm, setJobSearchTerm] = useState('');

	// Multi-bar state. One bar rarely covers a big request, so the operator
	// commits what fits and comes back for the rest.
	const [committed, setCommitted] = useState<CommittedBar[]>([]);
	const [lastResult, setLastResult] =
		useState<CreateReservationResponse | null>(null);

	// One key per attempt. A gloved double-tap or a retry after flaky shop wifi
	// replays the first response instead of reserving the bar twice.
	const idempotencyKey = useRef<string>(crypto.randomUUID());
	// Which bar the current key was minted for, so switching bars mints a new
	// one (see onPickBar).
	const keyedForBar = useRef<string | null>(null);

	// Scope the hot list to this tablet's site. Sending it only once the
	// building has resolved avoids a first paint that shows every building's
	// requests and then drops half of them a moment later.
	const { buildingId, buildingName } = useBuildingContext();

	const {
		data: needsData,
		isLoading: isLoadingNeeds,
		isError: needsError,
		refetch: refetchNeeds,
	} = useGetShopFloorMaterialNeedsQuery(
		{
			...(hotListSearch ? { search: hotListSearch } : {}),
			...(buildingId ? { building_id: buildingId } : {}),
		},
		// Nobody is watching this screen waiting, but a request should not go
		// stale for long.
		{ pollingInterval: 30000 }
	);
	const needs = needsData || [];
	const activeNeed = useMemo(
		() => needs.find((n) => n.id === needId) || null,
		[needs, needId]
	);

	const [searchJobs, { data: jobResults, isFetching: isSearching }] =
		useLazySearchReservableJobsQuery();
	const [
		fetchCandidates,
		{ data: candidates, isFetching: isLoadingBars, error: candidatesError },
	] = useLazyGetMaterialNeedCandidatesQuery();
	const [createReservation, { isLoading: isSubmitting }] =
		useCreateShopFloorReservationMutation();
	const [dismissNeed, { isLoading: isDismissing }] =
		useDismissShopFloorMaterialNeedMutation();

	const { data: cutOptions } = useGetMaterialNeedCutOptionsQuery(
		{ order_line_item: job?.id || '' },
		{ skip: !job }
	);
	const multipleCuts = (cutOptions?.options?.length || 0) > 1;

	const reservedSoFar = committed.reduce((total, bar) => total + bar.pieces, 0);
	const remaining = Math.max(quantity - reservedSoFar, 0);
	const isComplete = reservedSoFar > 0 && remaining === 0;

	// A deep link from the hot list arrives with the job and piece count already
	// decided, so the operator lands straight on the material choice.
	useEffect(() => {
		if (activeNeed && !job) {
			setJob(activeNeed.order_line_item);
			setQuantity(activeNeed.remaining_quantity);
		}
	}, [activeNeed, job]);

	const resetFlow = () => {
		idempotencyKey.current = crypto.randomUUID();
		keyedForBar.current = null;
		setCommitted([]);
		setLastResult(null);
		setSubmitError(null);
		setShowOtherOptions(false);
	};

	const chooseJob = (
		selected: NeedOrderLineItem,
		pieces: number,
		need?: ShopFloorMaterialNeed
	) => {
		resetFlow();
		setJob(selected);
		setQuantity(pieces);
		setSourceReservation(undefined);
		// Kept in the URL so a refresh or a shared link lands on the same
		// request rather than back on the list.
		const next = new URLSearchParams(params);
		if (need) next.set('need', need.id);
		else next.delete('need');
		setParams(next, { replace: true });
	};

	const clearJob = () => {
		resetFlow();
		setJob(null);
		setSourceReservation(undefined);
		setJobSearchTerm('');
		const next = new URLSearchParams(params);
		next.delete('need');
		setParams(next, { replace: true });
	};

	const loadBars = (pieces: number) => {
		if (!job || !pieces) return;
		setShowOtherOptions(false);
		fetchCandidates({
			order_line_item: job.id,
			quantity: pieces,
			...(sourceReservation ? { source_reservation: sourceReservation } : {}),
		});
	};

	/** Pieces this bar can actually take from what is still outstanding. */
	const piecesFor = (bar: CandidateBar) => Math.min(remaining, bar.max_pieces);

	const submit = async (bar: CandidateBar, acknowledgedCert: boolean) => {
		if (!job) return;
		const pieces = piecesFor(bar);
		if (pieces < 1) return;

		setSubmitError(null);
		try {
			const response = await createReservation({
				values: {
					order_line_item: job.id,
					material_length: bar.material_length_id,
					quantity: pieces,
					...(sourceReservation
						? { source_reservation: sourceReservation }
						: {}),
					...(needId ? { need: needId } : {}),
					...(selectedUser
						? {
								operator_name:
									selectedUser.full_name || selectedUser.first_name,
						  }
						: {}),
					certificate_acknowledged: acknowledgedCert,
				},
				idempotencyKey: idempotencyKey.current,
			}).unwrap();

			setLastResult(response);
			setCommitted((previous) => [
				...previous,
				{
					label: response.material_friendly_id || barId(bar),
					location: barLocationLabel(bar.location, formatLocation) || 'No location',
					pieces,
				},
			]);

			// A fresh key for the next bar, and a fresh candidate list for what
			// is left -- availability moved when this reservation committed.
			idempotencyKey.current = crypto.randomUUID();
			keyedForBar.current = null;
			const stillNeeded = remaining - pieces;
			if (stillNeeded > 0) loadBars(stillNeeded);
		} catch (error: any) {
			// A 409 means someone else took the bar between listing and submit.
			// Refreshing the list is the useful response, not an error the
			// operator has to interpret.
			const detail =
				error?.data?.detail || 'Could not create the reservation.';
			setSubmitError(detail);
			if (error?.status === 409) {
				idempotencyKey.current = crypto.randomUUID();
				keyedForBar.current = null;
				loadBars(remaining);
			}
			// The hot-list count moved under us (someone else reserved part of
			// this request, or closed it). The stale number on screen is what
			// caused the refusal, so pull the list again rather than leaving
			// the operator to re-tap into the same error.
			const code = error?.data?.code;
			if (code === 'need_quantity_exceeded' || code === 'need_closed') {
				refetchNeeds();
			}
		} finally {
			setPendingBar(null);
			setCertBar(null);
		}
	};

	const onPickBar = (bar: CandidateBar, bucket: CandidateBucket) => {
		// A new key per bar, not merely per success. If a submit succeeded but
		// the response was lost on shop wifi, the operator sees an error and
		// may pick a different bar; reusing the key would replay the first
		// bar's stored response and record the wrong bar against this pick.
		if (bar.material_length_id !== keyedForBar.current) {
			idempotencyKey.current = crypto.randomUUID();
			keyedForBar.current = bar.material_length_id;
		}
		if (bucket.requires_certificate_ack) {
			setCertBar(bar);
			return;
		}
		setPendingBar(bar);
	};

	const confirmDismiss = async () => {
		if (!dismissing) return;
		try {
			await dismissNeed({ id: dismissing.id }).unwrap();
		} finally {
			setDismissing(null);
		}
	};

	const leavePage = () =>
		navigate(
			cameFromCutting
				? '/tablet/operations/cutting?group_cut_status=Pending'
				: '/tablet/operations'
		);

	const buckets = candidates?.buckets || [];
	const [bestBucket, ...otherBuckets] = buckets;
	const spec = candidates?.source_reservation;

	const shell: React.CSSProperties = {
		display: 'flex',
		flexDirection: 'column',
		height: '100vh',
		overflow: 'hidden',
	};

	// Everything is reserved: the operator is done and needs the queue message,
	// not another list of bars.
	if (isComplete) {
		return (
			<main
				data-testid="page-fastlane-reservations"
				data-ready="true"
				className="w-full p-3"
				style={{ ...shell, overflowY: 'auto' }}
			>
				<Result
					status="success"
					icon={<CheckCircleOutlined />}
					title={`Reserved ${reservedSoFar} pcs for ${
						job?.order_name || 'this job'
					}`}
					subTitle={
						<div style={{ fontSize: 15, marginTop: 12 }}>
							<Paragraph style={{ marginBottom: 8 }}>
								{lastResult?.queue.message}
							</Paragraph>
							<CommittedList committed={committed} />
							{lastResult && !lastResult.queue.on_cutting_queue && (
								<Alert
									type="warning"
									showIcon
									style={{ marginTop: 16, textAlign: 'left' }}
									message="Not on the cutting queue yet"
									description="Tell the shop manager or programmer that CAM Programming still needs to be completed for this job."
								/>
							)}
							<Alert
								type="info"
								showIcon
								style={{ marginTop: 16, textAlign: 'left' }}
								message="The cut material still has to be delivered"
								description="It travels with the normal kitting and delivery flow. Let the machine operator know to expect it there rather than waiting at the saw."
							/>
						</div>
					}
					extra={[
						<Button
							key="another"
							size="large"
							onClick={clearJob}
							data-testid="reserve-another"
						>
							Reserve for another job
						</Button>,
						<Button
							key="done"
							type="primary"
							size="large"
							onClick={leavePage}
						>
							Done
						</Button>,
					]}
				/>
			</main>
		);
	}

	return (
		<main
			data-testid="page-fastlane-reservations"
			data-ready={!isLoadingNeeds ? 'true' : 'false'}
			className="w-full p-3"
			style={shell}
		>
			<div style={{ flexShrink: 0 }}>
				<TabletHeader
					title="Reservations"
					showOnClickButton
					icon={<ArrowLeftOutlined />}
					buttonText="Back"
					actionButtonStyle={TABLET_BACK_BUTTON_STYLE}
					// With a job chosen, Back means "pick a different one" --
					// leaving the page entirely is a bigger jump than the
					// operator asked for.
					handleOnClick={job ? clearJob : leavePage}
					{...(job
						? {}
						: {
								searchStyle: TABLET_SEARCH_STYLE,
								searchPlaceholder: 'Job number, part, machine',
								onSearch: (value: string) =>
									setHotListSearch(value || ''),
						  })}
				/>
			</div>

			{job ? (
				<JobFlow
					job={job}
					quantity={quantity}
					remaining={remaining}
					committed={committed}
					candidates={candidates}
					candidatesError={candidatesError}
					spec={spec}
					bestBucket={bestBucket}
					otherBuckets={otherBuckets}
					showOtherOptions={showOtherOptions}
					setShowOtherOptions={setShowOtherOptions}
					isLoadingBars={isLoadingBars}
					isSubmitting={isSubmitting}
					submitError={submitError}
					multipleCuts={multipleCuts}
					reportedMachine={activeNeed?.machine_name}
					cutOptions={cutOptions?.options}
					sourceReservation={sourceReservation}
					setSourceReservation={setSourceReservation}
					setQuantity={setQuantity}
					onFindMaterial={() => loadBars(remaining)}
					onChangeJob={clearJob}
					onPickBar={onPickBar}
					recommendedId={
						candidates?.recommended_material_length_id || null
					}
				/>
			) : (
				<HotList
					needs={needs}
					isLoading={isLoadingNeeds}
					isError={needsError}
					refetch={refetchNeeds}
					search={hotListSearch}
					jobSearchTerm={jobSearchTerm}
					setJobSearchTerm={setJobSearchTerm}
					onSearchJobs={(value) => searchJobs({ search: value })}
					buildingName={buildingName}
					isSearching={isSearching}
					jobResults={jobResults?.results}
					onPickNeed={(need) =>
						chooseJob(
							need.order_line_item,
							need.remaining_quantity,
							need
						)
					}
					onPickJob={(option) => chooseJob(option, 1)}
					onDismiss={setDismissing}
				/>
			)}

			{/* Plain confirmation for a safe pick */}
			<Modal
				open={!!pendingBar}
				title="Reserve this material?"
				okText={
					pendingBar ? `Reserve ${piecesFor(pendingBar)} pcs` : 'Reserve'
				}
				okButtonProps={{ loading: isSubmitting, size: 'large' }}
				cancelButtonProps={{ size: 'large' }}
				onOk={() => pendingBar && submit(pendingBar, false)}
				onCancel={() => setPendingBar(null)}
				centered
			>
				{pendingBar && (
					<BarSummary
						bar={pendingBar}
						pieces={piecesFor(pendingBar)}
						remaining={remaining}
					/>
				)}
			</Modal>

			{/* Uncertified substitute: warn, name who to call, record the ack */}
			<Modal
				open={!!certBar}
				title={
					<span>
						<WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
						No material certificate on file
					</span>
				}
				okText="I have notified them — reserve it"
				okButtonProps={{ danger: true, loading: isSubmitting, size: 'large' }}
				cancelButtonProps={{ size: 'large' }}
				onOk={() => certBar && submit(certBar, true)}
				onCancel={() => setCertBar(null)}
				centered
				width={620}
			>
				<Paragraph style={{ fontSize: 15 }}>
					This material matches what the job needs, but there is no material
					certificate on file for it yet.
				</Paragraph>
				<Paragraph style={{ fontSize: 15 }}>
					<Text strong>
						Tell the shop manager or the programmer before you cut it.
					</Text>{' '}
					Your name and this bar are recorded so the certificate can be
					chased later.
				</Paragraph>
				{certBar && (
					<BarSummary
						bar={certBar}
						pieces={piecesFor(certBar)}
						remaining={remaining}
					/>
				)}
				{!selectedUser && (
					<Alert
						type="warning"
						showIcon
						style={{ marginTop: 12 }}
						message="No operator selected on this tablet"
						description="Pick your name in the header first so the acknowledgement is recorded against you."
					/>
				)}
			</Modal>

			<Modal
				open={!!dismissing}
				title="Dismiss this request?"
				okText="Dismiss request"
				okButtonProps={{ danger: true, loading: isDismissing }}
				onOk={confirmDismiss}
				onCancel={() => setDismissing(null)}
				centered
			>
				<Paragraph>
					This removes the request from the list without reserving any
					material. The machine operator is not notified, so only dismiss it
					if you know the material is no longer needed.
				</Paragraph>
				{dismissing && (
					<Text strong>
						{dismissing.order_line_item.order_name} —{' '}
						{dismissing.remaining_quantity} pcs
					</Text>
				)}
			</Modal>
		</main>
	);
};

/**
 * Landing state: what the mills have asked for, then a search for any other
 * job. This half scrolls on its own so the header stays put.
 */
const HotList: React.FC<{
	needs: ShopFloorMaterialNeed[];
	isLoading: boolean;
	isError: boolean;
	refetch: () => void;
	search: string;
	jobSearchTerm: string;
	setJobSearchTerm: (value: string) => void;
	onSearchJobs: (value: string) => void;
	isSearching: boolean;
	jobResults?: NeedOrderLineItem[];
	buildingName: string | null;
	onPickNeed: (need: ShopFloorMaterialNeed) => void;
	onPickJob: (job: NeedOrderLineItem) => void;
	onDismiss: (need: ShopFloorMaterialNeed) => void;
}> = ({
	needs,
	isLoading,
	isError,
	refetch,
	search,
	jobSearchTerm,
	setJobSearchTerm,
	onSearchJobs,
	isSearching,
	jobResults,
	buildingName,
	onPickNeed,
	onPickJob,
	onDismiss,
}) => (
	<div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
		<SectionLabel>
			{buildingName
				? `Material requested by machine operators in ${buildingName}. Tap a request to reserve material for it.`
				: 'Material requested by machine operators. Tap a request to reserve material for it.'}
		</SectionLabel>

		{isError && (
			<Alert
				type="error"
				showIcon
				message="Could not load requests."
				action={
					<Button size="small" onClick={refetch}>
						Retry
					</Button>
				}
				style={{ marginBottom: 12 }}
			/>
		)}

		{isLoading ? (
			<div className="flex justify-center" style={{ padding: 60 }}>
				<Spin size="large" />
			</div>
		) : needs.length === 0 ? (
			<Empty
				style={{ marginTop: 24 }}
				description={
					search
						? 'No requests match that search.'
						: buildingName
							? `No open material requests in ${buildingName}. Search below to reserve material for any job.`
							: 'No open material requests. Search below to reserve material for any job.'
				}
			/>
		) : (
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))',
					gap: 12,
				}}
			>
				{needs.map((need) => (
					<div
						key={need.id}
						data-testid="material-need-card"
						style={{
							border: '1px solid #e8e8e8',
							borderLeft: '6px solid #fa8c16',
							borderRadius: 8,
							padding: 14,
							background: '#fff',
						}}
					>
						<div className="flex justify-between items-start gap-3">
							<div className="flex gap-3 items-start" style={{ minWidth: 0 }}>
								<PartThumbnail job={need.order_line_item} />
								<div style={{ minWidth: 0 }}>
									<Title level={5} style={{ margin: 0 }}>
										{need.order_line_item.order_name || 'Unknown job'}
									</Title>
									<Text type="secondary" style={{ fontSize: 14 }}>
										{need.order_line_item.name || '—'}
									</Text>
								</div>
							</div>
							<Tag
								color="orange"
								style={{ fontSize: 17, padding: '2px 10px', margin: 0 }}
							>
								{need.remaining_quantity} pcs
							</Tag>
						</div>

						<div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.8 }}>
							<div>
								<Text type="secondary">Reason: </Text>
								{need.reason_label || 'Not given'}
							</div>
							<div>
								<Text type="secondary">Machine: </Text>
								{need.machine_name || (
									<MachineTag job={need.order_line_item} />
								)}{' '}
								{/* The list is already scoped, so a tag repeating the
								    tablet's own building on every card is noise. It earns
								    its place only when the list is unscoped, or when the
								    site did not resolve and the request is therefore
								    showing to operators who may not own it. */}
								{!need.building_name ? (
									<Tooltip
										title={
											need.source_building_name
												? `The Shop Floor App reported "${need.source_building_name}", which does not match a building here. This request shows on every saw tablet — check the machine before reserving.`
												: 'This request arrived without a building, so it shows on every saw tablet. Check the machine before reserving.'
										}
									>
										<Tag color="warning" style={{ fontSize: 13 }}>
											<WarningOutlined /> Site unknown
										</Tag>
									</Tooltip>
								) : (
									!buildingName && (
										<BuildingTag
											name={need.building_name}
											style={{ fontSize: 13 }}
										/>
									)
								)}
							</div>
							<div>
								<Text type="secondary">Requested by: </Text>
								{need.requested_by_name || '—'}{' '}
								<Text type="secondary">
									· {relativeAge(need.created_time)}
								</Text>
							</div>
							{need.remaining_quantity !== need.requested_quantity && (
								<div>
									<Text type="secondary">
										Partially reserved — {need.requested_quantity} pcs
										originally requested.
									</Text>
								</div>
							)}
							{need.notes && (
								<div style={{ marginTop: 4 }}>
									<Text italic>“{need.notes}”</Text>
								</div>
							)}
						</div>

						<div className="flex gap-3" style={{ marginTop: 12 }}>
							<Button
								type="primary"
								size="large"
								block
								data-testid="material-need-reserve"
								onClick={() => onPickNeed(need)}
							>
								Reserve material
							</Button>
							<Button
								size="large"
								onClick={() => onDismiss(need)}
								data-testid="material-need-dismiss"
							>
								Dismiss
							</Button>
						</div>
					</div>
				))}
			</div>
		)}

		<Divider style={{ margin: '16px 0 12px' }}>or reserve for any job</Divider>

		<SectionLabel>Search by job number, part name or machine</SectionLabel>
		<Input.Search
			size="large"
			allowClear
			placeholder="e.g. CP22844NZ, bracket, Gimli"
			style={{ maxWidth: 520 }}
			value={jobSearchTerm}
			onChange={(e) => setJobSearchTerm(e.target.value)}
			onSearch={(value) => {
				if (value && value.trim().length >= 2) onSearchJobs(value.trim());
			}}
			loading={isSearching}
			data-testid="job-search"
		/>

		{jobResults && (
			<div style={{ marginTop: 12, maxWidth: 720 }}>
				{jobResults.length === 0 ? (
					<Empty
						image={null}
						description="No job with a material reservation matches that. Only jobs that already have material reserved can get an extra."
					/>
				) : (
					jobResults.map((option) => (
						<Button
							key={option.id}
							size="large"
							block
							data-testid="job-search-option"
							style={{
								height: 'auto',
								padding: 12,
								textAlign: 'left',
								whiteSpace: 'normal',
								marginBottom: 8,
							}}
							onClick={() => onPickJob(option)}
						>
							<div className="flex gap-3 items-center">
								<PartThumbnail job={option} size={48} />
								<div style={{ minWidth: 0 }}>
									<div style={{ fontSize: 16, fontWeight: 600 }}>
										{option.order_name}
									</div>
									<div style={{ fontSize: 13 }}>
										{option.name} · <MachineTag job={option} /> ·{' '}
										{option.status || '—'}
									</div>
								</div>
							</div>
						</Button>
					))
				)}
			</div>
		)}
	</div>
);

/**
 * Working state, laid out for a tablet held in landscape: the job, the piece
 * count and the locked material spec pinned across the top, and only the bar
 * options scrolling underneath. The operator is walking a rack with this in
 * hand, so what they are reserving for must not scroll away.
 */
const JobFlow: React.FC<{
	job: NeedOrderLineItem;
	quantity: number;
	remaining: number;
	committed: CommittedBar[];
	candidates?: CandidatesResponse;
	candidatesError: unknown;
	spec?: SourceReservationSpec;
	bestBucket?: CandidateBucket;
	otherBuckets: CandidateBucket[];
	showOtherOptions: boolean;
	setShowOtherOptions: (value: boolean) => void;
	isLoadingBars: boolean;
	isSubmitting: boolean;
	submitError: string | null;
	multipleCuts: boolean;
	reportedMachine?: string | null;
	cutOptions?: { reservation_id: string; material_cut_length: string; material_display: string }[];
	sourceReservation?: string;
	setSourceReservation: (value: string) => void;
	setQuantity: (value: number) => void;
	onFindMaterial: () => void;
	onChangeJob: () => void;
	onPickBar: (bar: CandidateBar, bucket: CandidateBucket) => void;
	recommendedId: string | null;
}> = ({
	job,
	quantity,
	remaining,
	committed,
	candidates,
	candidatesError,
	spec,
	bestBucket,
	otherBuckets,
	showOtherOptions,
	setShowOtherOptions,
	isLoadingBars,
	isSubmitting,
	submitError,
	multipleCuts,
	reportedMachine,
	cutOptions,
	sourceReservation,
	setSourceReservation,
	setQuantity,
	onFindMaterial,
	onChangeJob,
	onPickBar,
	recommendedId,
}) => (
	<div
		data-testid="page-fastlane-create-reservation"
		data-ready="true"
		style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
	>
		{/* Top zone: never scrolls */}
		<div
			style={{
				flexShrink: 0,
				display: 'grid',
				gridTemplateColumns: 'minmax(300px, 1.2fr) minmax(240px, 0.8fr) minmax(320px, 1.4fr)',
				gap: 12,
				alignItems: 'stretch',
			}}
		>
			<Card
				title={<span style={CARD_TITLE}>1. Job</span>}
				size="small"
				styles={{ body: { padding: 14 } }}
			>
				<div className="flex gap-3 items-start">
					<PartThumbnail job={job} />
					<div style={{ minWidth: 0, flex: 1 }}>
						<div style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.25 }}>
							{jobLabel(job)}
						</div>
						<div
							className="flex flex-col"
							style={{ marginTop: 4, gap: 2 }}
						>
							<Field label="Machine:">
								<MachineTag job={job} reported={reportedMachine} />
							</Field>
							<div className="flex gap-4 flex-wrap">
								<Field label="Order Qty:">{job.quantity ?? '—'}</Field>
								<Field label="Status:">{job.status || '—'}</Field>
							</div>
						</div>
						<Button
							size="small"
							onClick={onChangeJob}
							style={{ marginTop: 8 }}
							data-testid="change-job"
						>
							Change job
						</Button>
					</div>
				</div>
			</Card>

			<Card
				title={<span style={CARD_TITLE}>2. Pieces needed</span>}
				size="small"
				styles={{ body: { padding: 14 } }}
			>
				<div className="flex items-center gap-3 flex-wrap">
					<InputNumber
						size="large"
						min={1}
						max={10000}
						value={quantity}
						onChange={(value) => setQuantity(Number(value) || 1)}
						// Locked once a bar is committed: lowering it below what
						// has already been reserved would drive "still needed" to
						// zero and show the request as finished when it is not.
						// Change job to start over.
						disabled={committed.length > 0}
						style={{ width: 110, fontSize: 19 }}
						data-testid="reservation-quantity"
					/>
					<Button
						type="primary"
						size="large"
						onClick={onFindMaterial}
						loading={isLoadingBars}
						data-testid="find-material"
					>
						Find material
					</Button>
				</div>

				{multipleCuts && (
					<div style={{ marginTop: 10 }}>
						<SectionLabel>
							Reserved at more than one cut length — pick the one to repeat
						</SectionLabel>
						<Select
							size="large"
							style={{ width: '100%' }}
							value={sourceReservation || cutOptions?.[0]?.reservation_id}
							onChange={setSourceReservation}
							options={(cutOptions || []).map((option) => ({
								value: option.reservation_id,
								label: `${option.material_cut_length}" — ${option.material_display}`,
							}))}
						/>
					</div>
				)}

				{committed.length > 0 && (
					<div style={{ marginTop: 10 }}>
						<Tag color="green" style={{ fontSize: 14 }}>
							{quantity - remaining} of {quantity} reserved
						</Tag>
						<Tag color="orange" style={{ fontSize: 14 }}>
							{remaining} still needed
						</Tag>
					</div>
				)}
			</Card>

			<Card
				title={<span style={CARD_TITLE}>3. Material required</span>}
				size="small"
				styles={{ body: { padding: 14 } }}
			>
				{spec ? (
					<div className="flex gap-3 items-center">
						<div
							style={{
								width: 104,
								height: 56,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								border: '1px solid #f0f0f0',
								borderRadius: 6,
								flexShrink: 0,
							}}
						>
							<ShapePreview source={spec} width={92} height={44} />
						</div>
						<div className="flex flex-col gap-1" style={{ minWidth: 0 }}>
							<div style={{ fontSize: 15 }}>
								<strong style={{ color: '#222' }}>
									{[spec.type, spec.shape, spec.display_size]
										.filter(Boolean)
										.join(' · ') || '—'}
								</strong>
							</div>
							<div className="flex flex-wrap gap-x-4 gap-y-1 items-center">
								<Field label="Cut Len:">{spec.material_cut_length}"</Field>
								<Field label="Saw Kerf:">{spec.saw_kerf}"</Field>
								{/* Explicit compare, not a truthiness check: the API
								    sends this as a number, and `{0 && ...}` puts a
								    bare "0" on screen next to the kerf. */}
								{Number(spec.required_trim_cut_length) > 0 && (
									<Field label="Trim Cut:">
										{spec.required_trim_cut_length}"
									</Field>
								)}
								<Field label="Needs Free:">
									{inches(candidates?.required_length)}
								</Field>
								{spec.is_swiss && <Tag color="blue">Swiss</Tag>}
							</div>
						</div>
					</div>
				) : (
					<Text type="secondary" style={{ fontSize: 14 }}>
						Set the piece count and tap Find material. The cut length, kerf
						and trim are copied from this job's existing reservation, so the
						saw repeats a cut it has already made.
					</Text>
				)}
			</Card>
		</div>

		{/* Bottom zone: the only part that scrolls */}
		<div
			style={{
				flex: 1,
				minHeight: 0,
				overflowY: 'auto',
				marginTop: 12,
				paddingRight: 4,
			}}
		>
			{candidatesError ? (
				<Alert
					type="error"
					showIcon
					message={
						(candidatesError as any)?.data?.detail ||
						'Could not look up material for this job.'
					}
				/>
			) : null}

			{submitError && (
				<Alert
					type="error"
					showIcon
					message={submitError}
					data-testid="reservation-error"
					style={{ marginBottom: 12 }}
				/>
			)}

			{committed.length > 0 && (
				<Alert
					type="success"
					showIcon
					style={{ marginBottom: 12 }}
					data-testid="reservation-progress"
					message={`${quantity - remaining} of ${quantity} pcs reserved — ${remaining} still to go`}
					description={<CommittedList committed={committed} />}
				/>
			)}

			{isLoadingBars && (
				<div className="flex justify-center" style={{ padding: 40 }}>
					<Spin size="large" />
				</div>
			)}

			{candidates && !isLoadingBars && (
				<>
					{candidates.empty_reason ? (
						<Alert
							type="warning"
							showIcon
							message="No material available for this request"
							description={candidates.empty_reason.message}
						/>
					) : (
						<div className="flex flex-col gap-3">
							{!candidates.full_coverage_available && (
								<Alert
									type="info"
									showIcon
									data-testid="no-single-bar-banner"
									message={`No single bar covers all ${remaining} pcs`}
									description="Reserve what fits off one bar, then pick another for the rest. Each bar you pick is committed on its own."
								/>
							)}

							{bestBucket && (
								<BucketList
									bucket={bestBucket}
									onPick={onPickBar}
									disabled={isSubmitting}
									remaining={remaining}
									recommendedId={recommendedId}
								/>
							)}

							{otherBuckets.length > 0 && !showOtherOptions && (
								<Button
									size="large"
									block
									style={{ height: 48, fontSize: 16 }}
									onClick={() => setShowOtherOptions(true)}
									data-testid="show-other-options"
								>
									Show other options (
									{otherBuckets.reduce((n, b) => n + b.bars.length, 0)} more
									bars)
								</Button>
							)}

							{showOtherOptions &&
								otherBuckets.map((bucket) => (
									<BucketList
										key={bucket.key}
										bucket={bucket}
										onPick={onPickBar}
										disabled={isSubmitting}
										remaining={remaining}
										recommendedId={recommendedId}
									/>
								))}
						</div>
					)}
				</>
			)}
		</div>
	</div>
);

const CommittedList: React.FC<{ committed: CommittedBar[] }> = ({
	committed,
}) => (
	<div style={{ textAlign: 'left' }}>
		{committed.map((bar, index) => (
			<div key={`${bar.label}-${index}`} style={{ fontSize: 14 }}>
				<strong>
					{bar.pieces} {bar.pieces === 1 ? 'pc' : 'pcs'}
				</strong>{' '}
				from {bar.label} at {bar.location}
			</div>
		))}
	</div>
);

/** How much of what is still outstanding a bar can take, said plainly. */
const CoverageTag: React.FC<{ bar: CandidateBar; remaining: number }> = ({
	bar,
	remaining,
}) => {
	const pieces = Math.min(remaining, bar.max_pieces);
	if (bar.covers_full_request) {
		return (
			<Tag color="green" style={{ fontSize: 14 }}>
				Covers all {remaining} pcs
			</Tag>
		);
	}
	return (
		<Tag color="orange" style={{ fontSize: 14 }}>
			Good for {pieces} — short {remaining - pieces}
		</Tag>
	);
};

const BarSummary: React.FC<{
	bar: CandidateBar;
	pieces: number;
	remaining: number;
}> = ({ bar, pieces, remaining }) => (
	<div style={{ marginTop: 8 }}>
		<div className="flex gap-4 items-start">
			<ShapePreview source={bar} />
			<div className="flex flex-col items-start gap-2" style={{ minWidth: 0 }}>
				<div>
					<BarLocation location={bar.location} size={17} />
				</div>
				<Field label="Bar ID">{barId(bar)}</Field>
				<div className="flex flex-wrap gap-x-6 gap-y-2">
					<Field label="Curr. Len">{inches(bar.current_length)}</Field>
					<Field label="Avail.">{inchesLeft(bar.available_length)}</Field>
				</div>
				<BuildingTag name={bar.location.building} style={{ fontSize: 13 }} />
			</div>
		</div>
		{pieces < remaining ? (
			<Alert
				type="warning"
				showIcon
				style={{ marginTop: 12 }}
				message={`This reserves ${pieces} of ${remaining} pcs`}
				description={`This bar cannot cover the rest. After this you will need to pick another bar for the remaining ${
					remaining - pieces
				} pcs.`}
			/>
		) : (
			<Text type="secondary" style={{ fontSize: 14 }}>
				Good for {bar.max_pieces} pcs — reserving {pieces}.
			</Text>
		)}
	</div>
);

/**
 * One ranked group of bars. Location leads because that is what the operator
 * physically acts on -- a list without it just sends them hunting.
 */
const BucketList: React.FC<{
	bucket: CandidateBucket;
	onPick: (bar: CandidateBar, bucket: CandidateBucket) => void;
	disabled: boolean;
	remaining: number;
	recommendedId: string | null;
}> = ({ bucket, onPick, disabled, remaining, recommendedId }) => (
	<Card
		title={
			<div className="flex items-center gap-3 flex-wrap">
				<span style={CARD_TITLE}>{bucket.label}</span>
				{bucket.requires_certificate_ack && (
					<Tag color="warning" icon={<WarningOutlined />}>
						Needs a heads-up first
					</Tag>
				)}
			</div>
		}
		size="small"
		styles={{ body: { padding: 14 } }}
		data-testid={`bucket-${bucket.key}`}
	>
		<div
			style={{
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
				gap: 10,
			}}
		>
			{bucket.bars.map((bar) => {
				const isRecommended = bar.material_length_id === recommendedId;
				return (
					<Button
						key={bar.material_length_id}
						size="large"
						disabled={disabled}
						data-testid="candidate-bar"
						data-recommended={isRecommended ? 'true' : 'false'}
						data-covers-full={bar.covers_full_request ? 'true' : 'false'}
						style={{
							height: 'auto',
							padding: 14,
							textAlign: 'left',
							whiteSpace: 'normal',
							...(isRecommended
								? {
										borderColor: '#52c41a',
										borderWidth: 2,
										background: '#f6ffed',
								  }
								: {}),
						}}
						onClick={() => onPick(bar, bucket)}
					>
						<div className="flex gap-3 items-start">
							<ShapePreview source={bar} />
							<div style={{ flex: 1, minWidth: 0 }}>
								<BarLocation location={bar.location} />
								<div style={{ marginTop: 4 }}>
									<Field label="Bar ID">{barId(bar)}</Field>
								</div>
								<div
									className="flex flex-wrap gap-x-4 gap-y-1"
									style={{ marginTop: 2 }}
								>
									<Field label="Curr. Len">
										{inches(bar.current_length)}
									</Field>
									<Field label="Avail.">
										{inchesLeft(bar.available_length)}
									</Field>
								</div>
								<div
									className="flex items-center gap-2 flex-wrap"
									style={{ marginTop: 6 }}
								>
									<CoverageTag bar={bar} remaining={remaining} />
									{bar.is_remnant && <Tag color="default">Remnant</Tag>}
									<BuildingTag
										name={bar.location.building}
										style={{ fontSize: 13 }}
									/>
								</div>
								{isRecommended && (
									<div
										style={{
											marginTop: 4,
											fontSize: 13,
											color: '#389e0d',
											fontWeight: 600,
										}}
									>
										Best single bar — no second saw setup
									</div>
								)}
							</div>
						</div>
					</Button>
				);
			})}
		</div>
	</Card>
);

export default Reservations;
